/**
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */

import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router';

type NavState = any;

export default function usePreserveSearchNav() {
  const history = useHistory();
  const { search, hash, pathname } = useLocation();

  const pushPreserve = useCallback(
    (pathname: string, state?: NavState) => {
      history.push({ pathname, search, hash }, state);
    },
    [history, search, hash]
  );

  const pushPreserveWithSearch = useCallback(
    (nextPathname: string, nextSearch: string, state?: NavState) => {
      history.push({ pathname: nextPathname, search: nextSearch, hash }, state);
    },
    [history, hash]
  );

  const replacePreserve = useCallback(
    (pathname: string, state?: NavState) => {
      history.replace({ pathname, search, hash }, state);
    },
    [history, search, hash]
  );

  const goBackOrReplace = useCallback(
    (fallbackPathname: string, state?: NavState) => {
      if (history.length > 1) {
        history.goBack();
      } else {
        history.replace({ pathname: fallbackPathname, search, hash }, state);
      }
    },
    [history, search, hash]
  );

  const replaceQueryParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      const params = new URLSearchParams(search || '');
      let changed = false;

      Object.entries(updates).forEach(([key, value]) => {
        const current = params.get(key);
        if (value == null) {
          if (current !== null) {
            params.delete(key);
            changed = true;
          }
        } else {
          const next = String(value);
          if (current !== next) {
            params.set(key, next);
            changed = true;
          }
        }
      });

      if (!changed) return;

      const nextSearch = `?${params.toString()}`;
      history.replace({ pathname, search: nextSearch, hash });
    },
    [history, pathname, search, hash]
  );

  const replaceLoaLevel = useCallback(
    (level: number) => replaceQueryParams({ loaLevel: String(level) }),
    [replaceQueryParams]
  );

  return {
    pushPreserve,
    pushPreserveWithSearch,
    replacePreserve,
    goBackOrReplace,
    replaceLoaLevel,
    replaceQueryParams
  };
}
